import { Color, Counted, colorToRgbNumbers, rgbToColor } from "./Image";

export type Sample = Counted<number[]>;

function distanceSquared(aChannels: number[], bChannels: number[]): number {
  return aChannels.reduce((sum, aChannel, index) => {
    let bChannel = bChannels[index];
    let distance = aChannel - bChannel;
    return sum + distance * distance;
  }, 0);
}

function minIndex(values: number[]) {
  return values.reduce(
    (min, value, index) => (value < min.value ? { value, index } : min),
    { value: Infinity, index: undefined as undefined | number }
  ).index;
}

export function cluster(
  k: number,
  palette: Counted<Color>[],
  weighted: boolean
): {
  map: Map<Color, number>;
  palette: Counted<Color>[];
} {
  // clamp k to the number of points
  k = Math.min(k, palette.length);

  // shuffle k random entries to the front of the list
  for (let i = 0; i < k; i++) {
    const j = i + Math.floor(Math.random() * (k - i));
    [palette[i], palette[j]] = [palette[j], palette[i]];
  }

  const points = palette.map(({ count, value }) => ({
    count,
    value: colorToRgbNumbers(value),
  }));

  // slice out the k entries at the front of the list
  let means = points.slice(0, k);
  let map = new Map<Color, number>();

  for (let iteration = 0; iteration < 15; iteration++) {
    let oldMeans = means;
    let newMeans = oldMeans.map(() => ({ count: 0, value: [0, 0, 0] }));

    points.forEach((point, pointIndex) => {
      const distances = oldMeans.map((mean) =>
        distanceSquared(mean.value, point.value)
      );
      let index = minIndex(distances) || 0;
      map.set(palette[pointIndex].value, index);
      let mean = newMeans[index];
      const count = weighted ? point.count : 1;
      mean.count += count;
      point.value.forEach(
        (channel, index) => (mean.value[index] += count * channel)
      );
    });

    newMeans.forEach((newMean) =>
      newMean.value.forEach(
        (_, index) =>
          (newMean.value[index] = Math.floor(
            newMean.value[index] / newMean.count
          ))
      )
    );

    if (
      !newMeans.some((newMean, index) => {
        let oldMean = oldMeans[index];
        return (
          newMean.count !== oldMean.count ||
          newMean.value.some((newChannel, index) => {
            let oldChannel = oldMean.value[index];
            return newChannel !== oldChannel;
          })
        );
      })
    ) {
      break;
    }
    means = newMeans;
  }

  return {
    map,
    palette: means.map(({ count, value }) => ({
      count,
      value: rgbToColor(value),
    })),
  };
}
